import React from 'react';
import { Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import LegalNav from '../components/LegalNav';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Privacy Policy | LTV Production - Video Production Company</title>
        <meta name="description" content="Privacy Policy for LTV Production. Learn how we collect, use, and protect your personal data, including information about advertising and cookies." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/privacy-policy" />
      </Helmet>

      <LegalNav />
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-gray-400 mb-2">LTV Production LTD</p>
        <p className="text-gray-500 mb-8">Last updated: March 2026</p>
        
        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="mb-4">
              Welcome to LTV Production ("we," "our," or "us"), operated by LTV Production LTD, a company registered in the United Kingdom. We respect your privacy and are committed to protecting your personal data.
            </p>
            <p>
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website ltvproduction.com, use our video production services, or interact with our platforms including LTV VOD and LTV Live.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-medium text-white mb-3">2.1 Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Name, email address, and phone number when you contact us or create an account</li>
              <li>Billing address and payment information for service purchases</li>
              <li>Business information for corporate clients</li>
              <li>Profile information if you sign up for LTV VOD</li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>IP address and approximate geographic location</li>
              <li>Browser type, version, and settings</li>
              <li>Device information (type, operating system, screen resolution)</li>
              <li>Pages visited, time spent, and navigation patterns</li>
              <li>Referral source and exit pages</li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-3">2.3 Cookies and Tracking Technologies</h3>
            <p>
              We use cookies, web beacons, and similar technologies to collect information about your browsing activities. See Section 7 for detailed information about our use of cookies.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions</li>
              <li>Personalize and improve your experience</li>
              <li>Display relevant advertisements</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>
          
          {/* GOOGLE ADSENSE SECTION - MANDATORY FOR ADSENSE APPROVAL */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Google AdSense and Advertising</h2>
            <p className="mb-4">
              We use Google AdSense and Google Ad Manager to display advertisements on our website. This section explains how these services work and how they use your data.
            </p>
            
            <h3 className="text-xl font-medium text-white mb-3">4.1 How Google AdSense Works</h3>
            <p className="mb-4">
              Google AdSense is an advertising service that allows us to display ads on our website. Google, as a third-party vendor, uses cookies to serve ads based on your prior visits to our website and other websites on the Internet.
            </p>
            
            <h3 className="text-xl font-medium text-white mb-3">4.2 DoubleClick Cookie</h3>
            <p className="mb-4">
              Google's use of the DoubleClick cookie enables it and its partners to serve ads based on your visit to our site and/or other sites on the Internet. The DoubleClick cookie is used by Google in the ads served on the websites of its partners, such as websites displaying AdSense ads.
            </p>
            
            <h3 className="text-xl font-medium text-white mb-3">4.3 Personalized Advertising</h3>
            <p className="mb-4">
              Third-party vendors, including Google, use cookies to serve ads based on your previous visits to this website or other websites. This is known as interest-based or personalized advertising.
            </p>
            
            <h3 className="text-xl font-medium text-white mb-3">4.4 Data Collected for Advertising</h3>
            <p className="mb-4">Google and other advertising partners may collect:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Information about your visits to this and other websites</li>
              <li>Your interactions with ads</li>
              <li>Device identifiers</li>
              <li>IP address and location data</li>
              <li>Browsing history and search queries</li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-3">4.5 Opting Out of Personalized Ads</h3>
            <p className="mb-4">
              You can opt out of personalized advertising by visiting:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Google Ads Settings
                </a> - Manage your Google ad preferences
              </li>
              <li>
                <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Digital Advertising Alliance
                </a> - Opt out of interest-based ads
              </li>
              <li>
                <a href="https://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Network Advertising Initiative
                </a> - Opt out of targeted advertising
              </li>
              <li>
                <a href="https://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Your Online Choices (EU)
                </a> - European opt-out page
              </li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-3">4.6 Google's Privacy Policy</h3>
            <p>
              For more information about how Google uses data when you use our site, please visit{' '}
              <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                Google's Privacy & Terms page
              </a>.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Sharing and Third Parties</h2>
            <p className="mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Service Providers:</strong> Companies that perform services on our behalf (payment processing, hosting, email services, analytics)</li>
              <li><strong>Advertising Partners:</strong> Google AdSense, Google Ad Manager, and other advertising networks</li>
              <li><strong>Analytics Providers:</strong> Google Analytics and similar services to understand website usage</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="font-semibold">We do not sell your personal information to third parties.</p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your personal data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure storage with encryption at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication</li>
              <li>Employee training on data protection</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Cookies Policy</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to collect and track information about your browsing activities on our website.
            </p>
            
            <h3 className="text-xl font-medium text-white mb-3">7.1 Types of Cookies We Use</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Essential Cookies:</strong> Required for website functionality (authentication, security)</li>
              <li><strong>Performance Cookies:</strong> Help us understand how visitors use our website (analytics)</li>
              <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements and track ad performance</li>
            </ul>
            
            <h3 className="text-xl font-medium text-white mb-3">7.2 Managing Cookies</h3>
            <p className="mb-4">
              You can control cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>View cookies stored on your device</li>
              <li>Block all or specific cookies</li>
              <li>Delete existing cookies</li>
              <li>Set preferences for certain websites</li>
            </ul>
            <p>
              Note: Disabling cookies may affect the functionality of our website and the ads you see.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Your Rights (GDPR & UK GDPR)</h2>
            <p className="mb-4">If you are located in the UK or European Union, you have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
              <li><strong>Right to Restriction:</strong> Request limitation of processing</li>
              <li><strong>Right to Data Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@ltvproduction.com" className="text-purple-400 hover:underline">
                privacy@ltvproduction.com
              </a>
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Data Retention</h2>
            <p className="mb-4">We retain your personal data for different periods depending on the type:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Data:</strong> Until you request deletion or account closure</li>
              <li><strong>Transaction Records:</strong> 7 years for legal and tax compliance</li>
              <li><strong>Analytics Data:</strong> 26 months (Google Analytics default)</li>
              <li><strong>Marketing Data:</strong> Until you unsubscribe or withdraw consent</li>
              <li><strong>Contact Inquiries:</strong> 2 years after last communication</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
            <p>
              Our services are not directed to children under 13 years of age (or 16 in the EU). We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for such transfers, including Standard Contractual Clauses where required.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p className="mb-4">For privacy-related inquiries or to exercise your rights, contact us at:</p>
            <div className="bg-gray-900/50 rounded-lg p-6 space-y-3">
              <p><strong>LTV Production LTD</strong></p>
              <p>Data Protection Officer</p>
              <p>Email: <a href="mailto:privacy@ltvproduction.com" className="text-purple-400 hover:underline">privacy@ltvproduction.com</a></p>
              <p>General Inquiries: <a href="mailto:info@ltvproduction.com" className="text-purple-400 hover:underline">info@ltvproduction.com</a></p>
              <p>Address: London, United Kingdom</p>
            </div>
          </section>
          
          {/* Related Links */}
          <section className="pt-8 border-t border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Related Policies</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/terms-of-service" className="text-purple-400 hover:underline">Terms of Service</Link>
              <Link to="/cookie-policy" className="text-purple-400 hover:underline">Cookie Policy</Link>
              <Link to="/dmca" className="text-purple-400 hover:underline">DMCA Policy</Link>
              <Link to="/contact" className="text-purple-400 hover:underline">Contact Us</Link>
            </div>
          </section>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
